import type { Node, Edge } from '@xyflow/react';
import type {
  FieldMappingDocument,
  MMMCard,
  TriggerNodeData,
  ConditionNodeData,
  ActionNodeData,
  DefaultActionNodeData,
  EndNodeData,
  AddBranchNodeData,
} from '@/types/field-mapping-flow';

// ── Layout Constants ──

const COLUMN_WIDTH = 280;
const COLUMN_GAP = 60;
const ROW_TRIGGER = 0;
const ROW_CONDITION = 180;
const ROW_ACTION = 380;

// Height estimates for dynamic positioning (must match or exceed actual rendered sizes)
// Base: header (~48px) + container padding (~16px) + add-module button (~38px) + border/shadow (~4px) + buffer
const ACTION_BASE_HEIGHT = 150;
// Per card: card item (~65px) + arrow separator (~20px) + extra-text buffer (~10px)
const CARD_HEIGHT = 85;

// Vertical offsets below maxActionBottom for routing channels (must not overlap)
const BYPASS_CHANNEL_OFFSET = 30; // Solid action→End lines turn right here
const DASHED_CHANNEL_OFFSET = 70; // Dashed empty→DA lines turn horizontal here

// Gap between the lowest action node and the default-action node
const DEFAULT_ACTION_GAP = 120;

// Horizontal padding from the rightmost column edge to the bypass vertical
const BYPASS_OUTER_PAD = 60;

// Gap between Default Actions bottom and the merge-point horizontal
const MERGE_GAP = 50;

// Visible collector-line length from merge point down to End
const COLLECTOR_LENGTH = 80;

// ── Types ──

interface LayoutInput {
  fieldMappings: Record<string, FieldMappingDocument>;
  appType: 'messaging' | 'events';
  triggerLabel: string;
  /** Whether the SaaS app supports multiple module cards per branch */
  isMultiModule: boolean;
  /**
   * Target module from incoming capture settings (e.g., 'Leads' or a board ID).
   * null when the SaaS has no default module and none is selected yet.
   */
  targetModule: string | null;
  /**
   * Map from module values to display labels (e.g., board ID → board name for Monday.com).
   * Used to resolve technical identifiers to human-readable names in card display.
   */
  moduleLabelMap: Record<string, string>;
  /** Only sync existing records — skip creating new ones */
  ignoreNewCustomers: boolean;
  /** Only sync new records — skip existing ones */
  ignoreExistingCustomers: boolean;
}

interface LayoutOutput {
  nodes: Node[];
  edges: Edge[];
}

// ── Helpers ──

/** Extract MMM cards from a field mapping document, resolving module labels */
function getCardsFromMapping(mapping: FieldMappingDocument, moduleLabelMap: Record<string, string>): MMMCard[] {
  if (!mapping.fields) return [];

  if (mapping.isMMM) {
    const cards = Object.values(mapping.fields).filter(
      (val): val is MMMCard =>
        typeof val === 'object' && val !== null && 'cardId' in val
    );
    // Resolve displayLabel for cards that have a raw module value as their label
    // (e.g., Monday board ID instead of board name)
    return cards
      .map((card) => {
        const resolvedLabel = moduleLabelMap[card.saasFieldModuleName];
        if (resolvedLabel && (!card.displayLabel || card.displayLabel === card.saasFieldModuleName)) {
          return { ...card, displayLabel: resolvedLabel };
        }
        return card;
      })
      .sort((a, b) => (a.orderOf ?? 0) - (b.orderOf ?? 0));
  }

  if (mapping.saasFieldModuleName) {
    const fields = mapping.fields as Record<string, string>;
    return [
      {
        cardId: '__legacy__',
        saasFieldModuleName: mapping.saasFieldModuleName,
        displayLabel: moduleLabelMap[mapping.saasFieldModuleName] || mapping.saasFieldModuleName,
        orderOf: 0,
        fields: fields,
        fields_type: mapping.fields_type || {},
        ct: mapping.ct,
      },
    ];
  }

  return [];
}

/** Estimate rendered height of an action node based on card count */
function estimateActionHeight(cardCount: number): number {
  return ACTION_BASE_HEIGHT + Math.max(0, cardCount) * CARD_HEIGHT;
}

// ── Main Layout Function ──

export function computeFlowLayout(input: LayoutInput): LayoutOutput {
  const { fieldMappings, appType, triggerLabel, isMultiModule, targetModule, moduleLabelMap, ignoreNewCustomers, ignoreExistingCustomers } = input;
  const targetModuleLabel = targetModule ? (moduleLabelMap[targetModule] || targetModule) : null;
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Sort branches: specific conditions first (by ct asc), "---default" always last
  const sortedEntries = Object.entries(fieldMappings).sort(([, a], [, b]) => {
    if (a.filterValue === '---default') return 1;
    if (b.filterValue === '---default') return -1;
    return (a.ct || 0) - (b.ct || 0);
  });

  // Separate default from non-default branches
  const nonDefaultEntries = sortedEntries.filter(([, m]) => m.filterValue !== '---default');
  const defaultEntry = sortedEntries.find(([, m]) => m.filterValue === '---default') || null;

  const isEventApp = appType === 'events';
  const hasAddBranch = appType === 'messaging';

  // For event apps: "Default for All" condition branch is removed
  //   (replaced by the Default Actions node)
  // For messaging apps: "Default for All" condition branch remains as a regular column
  const branchEntries = isEventApp ? nonDefaultEntries : sortedEntries;

  // Default Actions node is shown for ALL app types
  const effectiveDefaultEntry = defaultEntry;

  const branchCount = branchEntries.length;

  // Total visual columns: branch columns + add-branch button (messaging only)
  const totalColumns = branchCount + (hasAddBranch ? 1 : 0);

  const totalWidth =
    totalColumns > 0
      ? totalColumns * COLUMN_WIDTH + (totalColumns - 1) * COLUMN_GAP
      : COLUMN_WIDTH;
  const startX = -totalWidth / 2;
  const triggerX = startX + totalWidth / 2 - COLUMN_WIDTH / 2;

  /** Get the X position for a given column index */
  function getColX(colIndex: number): number {
    return startX + colIndex * (COLUMN_WIDTH + COLUMN_GAP);
  }

  // ── Trigger Node (centered above all columns) ──
  nodes.push({
    id: 'trigger',
    type: 'trigger',
    position: { x: triggerX, y: ROW_TRIGGER },
    data: {
      type: 'trigger',
      label: triggerLabel,
      appType,
    } as TriggerNodeData,
    draggable: false,
    selectable: false,
  });

  // ── Non-Default Branch Columns ──
  let hasAnyActionNode = false;
  let maxActionBottom = ROW_ACTION;

  // Deferred channel edges for empty branches → Default Actions
  const deferredChannelEdges: Array<{ source: string; sourceColX: number }> = [];
  const deferredActionEndEdges: Array<{ actionNodeId: string }> = [];

  branchEntries.forEach(([docId, mapping], i) => {
    const colIndex = i;
    const x = getColX(colIndex);
    const cards = getCardsFromMapping(mapping, moduleLabelMap);
    const hasCards = cards.length > 0;
    const condNodeId = `condition-${docId}`;
    const isDefaultBranch = mapping.filterValue === '---default';

    // Condition node
    nodes.push({
      id: condNodeId,
      type: 'condition',
      position: { x, y: ROW_CONDITION },
      data: {
        type: 'condition',
        docId,
        filterValue: mapping.filterValue,
        filterValueLabel: isDefaultBranch
          ? (mapping.filterValueLabel || 'Default for All')
          : (mapping.filterValueLabel || mapping.name || mapping.filterValue),
        isDefault: isDefaultBranch,
        hasActions: hasCards,
      } as ConditionNodeData,
    });

    // Trigger → Condition
    edges.push({
      id: `e-trigger-${condNodeId}`,
      source: 'trigger',
      target: condNodeId,
      animated: false,
    });

    // Only show action node if the branch has cards configured.
    // Empty branches connect directly to the Default Actions node instead.
    // Default branch never gets a separate action node — its cards are shown
    // in the DEFAULT ACTIONS node (prevents duplicate action nodes).
    if (hasCards && !isDefaultBranch) {
      const actionNodeId = `action-${docId}`;
      nodes.push({
        id: actionNodeId,
        type: 'action',
        position: { x, y: ROW_ACTION },
        data: {
          type: 'action',
          docId,
          cards,
          isMMM: !!mapping.isMMM,
          isMultiModule,
          name: mapping.name,
          ignoreNewCustomers,
          ignoreExistingCustomers,
        } as ActionNodeData,
      });

      hasAnyActionNode = true;

      const actionBottom = ROW_ACTION + estimateActionHeight(cards.length);
      if (actionBottom > maxActionBottom) maxActionBottom = actionBottom;

      // Condition → Action (same column, straight down)
      edges.push({
        id: `e-${condNodeId}-${actionNodeId}`,
        source: condNodeId,
        target: actionNodeId,
        animated: false,
      });

      // Defer action→End edge (bypass routing computed after Default Actions positioning)
      deferredActionEndEdges.push({ actionNodeId });
    } else if (effectiveDefaultEntry) {
      // No actions — deferred dashed channel edge to Default Actions
      deferredChannelEdges.push({ source: condNodeId, sourceColX: x });
    }
  });

  // ── Default Actions Node ──
  let endY = maxActionBottom + 100;
  const defaultActionX = triggerX;
  const defaultActionY = maxActionBottom + DEFAULT_ACTION_GAP;
  let defaultActionBottom = maxActionBottom;

  if (effectiveDefaultEntry) {
    const [defaultDocId, defaultMapping] = effectiveDefaultEntry;
    const defaultCards = getCardsFromMapping(defaultMapping, moduleLabelMap);
    const defaultHasCards = defaultCards.length > 0;

    nodes.push({
      id: 'default-action',
      type: 'defaultAction',
      position: { x: defaultActionX, y: defaultActionY },
      data: {
        type: 'defaultAction',
        docId: defaultDocId,
        cards: defaultCards,
        isMMM: !!defaultMapping.isMMM,
        isMultiModule,
        name: defaultMapping.name,
        targetModule,
        targetModuleLabel,
        hasConfiguredCards: defaultHasCards,
        ignoreNewCustomers,
        ignoreExistingCustomers,
      } as DefaultActionNodeData,
      draggable: true,
      selectable: true,
    });

    hasAnyActionNode = true;

    // Default Actions → End (straight down — bypass edges arrive at End from the right side)
    edges.push({
      id: 'e-default-action-end',
      source: 'default-action',
      target: 'end',
      animated: false,
    });

    defaultActionBottom = defaultActionY + estimateActionHeight(defaultCards.length);
    endY = Math.max(endY, defaultActionBottom + COLLECTOR_LENGTH + MERGE_GAP);
  }

  // ── Action → End bypass edges (all share one side for shortest collective path) ──
  if (deferredActionEndEdges.length > 0 && effectiveDefaultEntry) {
    const rightEdge = Math.max(defaultActionX + COLUMN_WIDTH, startX + totalWidth);
    const leftEdge = Math.min(defaultActionX, startX);
    const rightOuterX = rightEdge + BYPASS_OUTER_PAD;
    const leftOuterX = leftEdge - BYPASS_OUTER_PAD;
    const daCenterX = defaultActionX + COLUMN_WIDTH / 2;

    // Decide ONE bypass direction for ALL actions based on their average position.
    // This ensures all action→End edges share the same path and merge cleanly.
    let sumCenterX = 0;
    for (const { actionNodeId } of deferredActionEndEdges) {
      const n = nodes.find((nd) => nd.id === actionNodeId);
      sumCenterX += n ? n.position.x + COLUMN_WIDTH / 2 : daCenterX;
    }
    const avgCenterX = sumCenterX / deferredActionEndEdges.length;
    const bypassOuterX = avgCenterX <= daCenterX ? leftOuterX : rightOuterX;

    const bypassChannelY = maxActionBottom + BYPASS_CHANNEL_OFFSET;
    const bypassMergeY = defaultActionBottom + MERGE_GAP;

    // Ensure End is far enough below the merge point for a visible collector line
    endY = Math.max(endY, bypassMergeY + COLLECTOR_LENGTH);

    for (const { actionNodeId } of deferredActionEndEdges) {
      edges.push({
        id: `e-${actionNodeId}-end`,
        source: actionNodeId,
        target: 'end',
        type: 'bypass',
        data: {
          outerX: bypassOuterX,
          channelY: bypassChannelY,
          mergeY: bypassMergeY,
        },
        animated: false,
      });
    }
  } else {
    // No Default Actions or no action branches — regular smoothstep edges
    for (const { actionNodeId } of deferredActionEndEdges) {
      edges.push({
        id: `e-${actionNodeId}-end`,
        source: actionNodeId,
        target: 'end',
        animated: false,
      });
    }
  }

  // ── Channel-routed edges (only for empty branches → Default Actions) ──
  if (deferredChannelEdges.length > 0) {
    const channelY = maxActionBottom + DASHED_CHANNEL_OFFSET;
    for (const def of deferredChannelEdges) {
      edges.push({
        id: `e-${def.source}-default-action`,
        source: def.source,
        target: 'default-action',
        type: 'channel',
        data: { channelY, dashed: true },
        animated: false,
      });
    }
  }

  // ── End Node (centered below all branches) ──
  const totalBranchCount = sortedEntries.length;
  if (totalBranchCount > 0 && (!isEventApp || hasAnyActionNode)) {
    nodes.push({
      id: 'end',
      type: 'end',
      position: { x: triggerX + COLUMN_WIDTH / 2 - 50, y: endY },
      data: { type: 'end' } as EndNodeData,
      draggable: true,
      selectable: true,
    });
  }

  // ── Add Branch Node (messaging only) ──
  if (hasAddBranch) {
    const addBranchColIndex = branchCount;
    const addBranchX = getColX(addBranchColIndex);

    nodes.push({
      id: 'add-branch',
      type: 'addBranch',
      position: {
        x: addBranchX + COLUMN_WIDTH / 2 - 20,
        y: ROW_CONDITION + 20,
      },
      data: { type: 'addBranch' } as AddBranchNodeData,
    });

    edges.push({
      id: 'e-trigger-add-branch',
      source: 'trigger',
      target: 'add-branch',
      animated: false,
      style: {
        strokeDasharray: '5,5',
        opacity: 0.6,
      },
    });
  }

  return { nodes, edges };
}
